import { Trans, useTranslation } from 'react-i18next';
import { useSuspenseQuery } from '@tanstack/react-query';
import { format, subMonths } from 'date-fns';
import { Alert } from '@c2fo/liquidity';
import { fetchClears } from '@/enterprise/data/useClearHistory';
import { fetchTakerHistory } from '@/enterprise/data/useTakerHistory';
import QueryBoundaries from '@/shared/components/QueryBoundaries';

const PENDING_CLEARS_SEARCH_MONTHS = 1;

interface MarketPendingClearsAlertProps {
  takerUuid: string;
  marketUuid: string;
  makerOrganizationUuid: string;
}

const AlertContent = () => {
  const { t } = useTranslation();

  return (
    <Alert
      type="info"
      full
      title={t('taker.history.pendingClearsAlert.title')}
      description={
        <div className="flex flex-col gap-1">
          <div>{t('taker.history.pendingClearsAlert.intro')}</div>
          <ul className="list-disc pl-4">
            <li>
              <Trans i18nKey="taker.history.pendingClearsAlert.processing" components={{ bold: <strong /> }} />
            </li>
            <li>
              <Trans i18nKey="taker.history.pendingClearsAlert.paymentSent" components={{ bold: <strong /> }} />
            </li>
          </ul>
        </div>
      }
    />
  );
};

const ConsolidatedPendingClearsAlertComponent = () => {
  const startDate = format(subMonths(new Date(), PENDING_CLEARS_SEARCH_MONTHS), 'yyyy-MM-dd');
  const { data } = useSuspenseQuery({
    queryKey: ['taker-history-pending-probe', startDate],
    queryFn: () => fetchTakerHistory({ startDate, endDate: '', sort: 'depositDate', pendingClear: true, limit: 1 }),
    staleTime: 1000 * 60 * 5,
  });

  if (!data?.length) {
    return null;
  }

  return <AlertContent />;
};

const MarketPendingClearsAlertComponent = ({
  takerUuid,
  marketUuid,
  makerOrganizationUuid,
}: MarketPendingClearsAlertProps) => {
  // 1 month in the past is a safe start date as there will be no pending clears older than that
  // The limit of 1 is not applied to the query in platform so adding a start date avoids long running queries
  const startDate = format(subMonths(new Date(), PENDING_CLEARS_SEARCH_MONTHS), 'yyyy-MM-dd');
  const { data } = useSuspenseQuery({
    queryKey: ['clear-history-pending-probe', takerUuid, marketUuid, makerOrganizationUuid, startDate],
    queryFn: () =>
      fetchClears(
        { isPendingClear: 'true', startDepositDate: startDate, limit: 1 },
        takerUuid,
        marketUuid,
        makerOrganizationUuid
      ),
    staleTime: 1000 * 60 * 5,
  });

  if (!data?.length) {
    return null;
  }

  return (
    <div className="px-4 py-6">
      <AlertContent />
    </div>
  );
};

export const ConsolidatedPendingClearsAlert = () => (
  <QueryBoundaries>
    <ConsolidatedPendingClearsAlertComponent />
  </QueryBoundaries>
);

export const MarketPendingClearsAlert = (props: MarketPendingClearsAlertProps) => (
  <QueryBoundaries>
    <MarketPendingClearsAlertComponent {...props} />
  </QueryBoundaries>
);
